<script setup>
import { defineProps, defineEmits, computed, ref } from 'vue';
import { NumberFormatUtil } from '@/plugins/app-util';

const props = defineProps({
    modelValue: [String, Number],
    name: String,
    placeholder: String,
    error: String,
    showErrorMessage: {
        type: Boolean,
        default: true,
    },
    full: Boolean,
    hFull: Boolean,
    width24: Boolean,
    width14: Boolean,
    width60: Boolean,
    width72: Boolean,
    height11: Boolean,
    height12: Boolean,
    width52: Boolean,
    width36: Boolean,
    readonly: {
        type: Boolean,
        default: false,
    },
    disabled: Boolean,
    isNumber: Boolean,
    inputName: String,
    inputtype: String,
    maxNumber: Number,
    minNumber: Number,
    maxlength: Number,
    autoComplete: {
        type: [String, Boolean],
    },
    height: {
    type: String,
    default: 'lg',
},
    required: Boolean,
    lastPaddingString: {
        type: String,
        default: null,
    },
    acceptFileFormat: Array,
    placeholderTextColorGray400: {
        type: Boolean,
        default: false,
    },
    isRequired: Boolean,
    isPhoneNum: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'afterInput']);
const toDigits = (v) => String(v ?? '').replace(/\D/g, '');

// 비밀번호 표시 상태 관리
const isPasswordVisible = ref(false);

// 실제 적용할 input type 계산
const currentInputType = computed(() => {
    if (props.inputtype === 'password') {
        return isPasswordVisible.value ? 'text' : 'password';
    }
    return props.inputtype || 'text';
});

// 비밀번호 토글 함수
const togglePasswordVisibility = () => {
    if (!props.disabled && !props.readonly) {
        isPasswordVisible.value = !isPasswordVisible.value;
    }
};

const formattedValue = computed({
    get() {
        if (!props.isPhoneNum) return String(props.modelValue ?? '');
        return NumberFormatUtil.convertPhoneGlobalToLocal(props.modelValue);
    },
    set(v) {
        if (props.isPhoneNum) {
            emit('update:modelValue', toDigits(v));
        } else {
            emit('update:modelValue', v ?? '');
        }
    },
});

function restrictToNumberIfPhoneNum(e) {
    if (!props.isPhoneNum) return; // 전화번호가 아닐 때는 제한 X

    const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Enter',
        'Home',
        'End',
    ];

    // 숫자(0~9)는 허용. 유저가 입력하는 숫자외 문자만 막을 뿐, 포맷팅할 때 발생하는 '+',' '는 허용
    const isNumberKey = /^[0-9]$/.test(e.key);
    const isAllowed = allowedKeys.includes(e.key);
    if (!isNumberKey && !isAllowed) {
        e.preventDefault(); // 숫자 외 키 입력 방지
    }
}
const heightClass = computed(() => {
switch (props.height) {
    case 'md':
        return 'h-[2.25rem]'
    case 'lg':
    default:
        return 'h-[2.75rem]'
}
});
</script>

<template>
    <div>
        <h1 v-if="name" class="c_label">
            {{ name }}
            <span class="text-accent-red-3" v-show="isRequired && !readonly">*</span>
        </h1>
        <div class="relative"
             :class="{
                'mt-2': name,
                'mt-0': !name,
                // --- 너비 클래스 이동 시작 ---
                // 'w-96': !full && !width24 && !width14 && !width60 && !width72 && !width52 && !width36,
                // 'w-60': width60,
                // 'w-full': full,
                // 'w-24': width24,
                // 'w-72': width72,
                // 'w-52': width52,
                // 'w-36': width36,
                // --- 너비 클래스 이동 끝 ---
                // 'h-full': hFull,
                'inline-block align-middle': lastPaddingString !== null, // 텍스트가 옆에 붙어야 할 경우
            }">
            <input
                class="block w-full border border-border-basic-2 rounded-2 font-body-sm-regular"
                :class="[heightClass,{
                    'focus:blu-text': !readonly,
                    'text-right': isNumber,
                    'border-accent-red-3': error,
                    'bg-background-basic-3 text-content-basic-1': disabled,
                    'bg-background-basic-3': readonly,
                    'placeholder-text-content-basic-3': placeholderTextColorGray400,
                    'pr-10': inputtype === 'password', /* 아이콘 공간 확보 */
                }]"
                :type="currentInputType"
                :placeholder="placeholder"
                v-model="formattedValue"
                :readonly="readonly"
                :disabled="disabled"
                :max="maxNumber"
                :min="minNumber"
                @blur="$emit('afterInput', $event)"
                :name="inputName"
                :maxlength="maxlength ? maxlength : 100"
                :autocomplete="autoComplete"
                :required="required"
                :accept="acceptFileFormat"
                @keydown="restrictToNumberIfPhoneNum"
            />

            <div
                v-if="inputtype === 'password'"
                class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                @click="togglePasswordVisibility"
            >
                <img
                    v-if="isPasswordVisible"
                    src="@/assets/img/eye_icon_text.svg"
                    alt="Hide Password"
                    class="h-5 w-5"
                />
                <img
                    v-else
                    src="@/assets/img/eye_icon_password.svg"
                    alt="Show Password"
                    class="h-5 w-5"
                />
            </div>

            <span v-if="lastPaddingString !== null" class="inline-block align-middle ml-5">{{
                lastPaddingString
            }}</span>
        </div>
        <p
            class="mt-1 font-caption-md-regular text-accent-red-3"
            v-show="error && typeof error == 'string' && showErrorMessage"
        >
            {{ error }}
        </p>
    </div>
</template>
<style scoped>
.blu {
    background: #4361ee;
}
.blu:hover {
    background: #334cba;
}
.blu-text {
    color: #4361ee;
}

input[type='file']::file-selector-button {
    background-color: white;
    transition: 1s;
    color: rgb(63 131 248);
    font-weight: 600;
}

input[type='file']::file-selector-button:hover {
    color: beige;
}

/* .placeholder-text-color-gray-400::placeholder {
    --tw-text-opacity: 1;
    color: rgb(156 163 175 / var(--tw-text-opacity));
} */
</style>
